import { memo } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { amber, blue, grey } from "@mui/material/colors";

import type {
  DraftSource,
  ElementCheck,
  Provenance,
} from "src/views/products/cap-ai/cr-generation/types";

import { useSmartCrEnabled } from "src/helpers/licensedRolloutGates";

import { getInnerResult } from "./toolStatusUtils";

/** The `cr_draft_created` tool result the Draft Condition Report tool returns.
 *
 * The result's own `path` is deliberately absent: the card builds the route
 * from `draft_id` instead, so a payload cannot point the Open button
 * somewhere else. */
export interface CrDraftCreatedResult {
  type: "cr_draft_created";
  draft_id: number;
  source: DraftSource;
  title?: string | null;
  fields: Array<{
    key: string;
    label: string;
    value: unknown;
    provenance: Provenance;
    lookup: boolean;
  }>;
  readiness: { ready: boolean; missing: string[]; missing_labels: string[] };
  /** The quality check's grades, in the plant's criteria order. */
  elements?: Array<{ key: string; label: string } & ElementCheck>;
  assessment?:
    | "assessed"
    | "capped"
    | "unparseable"
    | "unavailable"
    | "skipped";
  passes?: number;
  /** Every file on the draft, and the ones this call added. */
  attachments?: string[];
  attached?: string[];
  existing: boolean;
  message?: string;
}

/** Mirrors `cap_cr_draft.DRAFT_PATH` and `routes.jsx`. */
export const crDraftPath = (draftId: number) =>
  `/products/cap-ai/cr/draft/${draftId}`;

/** The id has to survive a round trip through the model's tool-call JSON, so
 * the card only links to one that could be a real draft row. */
const isCrDraftId = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value > 0;

/** `cap_cr_draft.TOOL_FUNCTION_NAME` — the registry turns the tool's display
 * name into its OpenAI function name, and either may reach the response. */
const CR_DRAFT_TOOL = "Draft_Condition_Report";

/**
 * The card for a tool response, or null when this response is not one.
 *
 * The result type alone does not qualify: the card is a link into the CAP
 * module, so only the tool that actually drafts a Condition Report may raise
 * it, and the id it links to has to be one a draft could have.
 */
export const selectCrDraftResult = (
  responseContent: any,
): CrDraftCreatedResult | null => {
  if (!responseContent || typeof responseContent !== "object") return null;
  const toolName = responseContent.tool_name;
  if (
    typeof toolName !== "string" ||
    toolName.replace(/[^a-zA-Z0-9]/g, "_") !== CR_DRAFT_TOOL
  ) {
    return null;
  }

  const inner = getInnerResult(responseContent);
  if (!inner || inner.type !== "cr_draft_created") return null;
  if (!isCrDraftId(inner.draft_id)) return null;

  return {
    ...inner,
    fields: Array.isArray(inner.fields) ? inner.fields : [],
    readiness: inner.readiness ?? {
      ready: true,
      missing: [],
      missing_labels: [],
    },
    existing: Boolean(inner.existing),
  } as CrDraftCreatedResult;
};

const MARK: Record<ElementCheck["status"], { glyph: string; color: string }> = {
  ok: { glyph: "✓", color: "#2e7d32" },
  weak: { glyph: "!", color: "#b28704" },
  missing: { glyph: "✕", color: "#c62828" },
};

const formatValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.map(String).join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return value === null || value === undefined ? "" : String(value);
};

/**
 * Hand-off card for an agent-authored Condition Report draft: what was
 * drafted, what the reviewer still owes, and the way to the draft. Rendered from the tool
 * result rather than the model's prose so the link and the gaps are never
 * left to the LLM to echo.
 */
const CrDraftCreatedCard = memo(
  ({ result }: { result: CrDraftCreatedResult }) => {
    // Its Open button leads to a Smart CR page; with the smart-cr license or
    // flag off that page redirects home, so the card is not shown at all.
    const smartCrEnabled = useSmartCrEnabled();
    if (!smartCrEnabled) return null;
    const heading = result.existing
      ? `Condition Report draft #${result.draft_id} revised`
      : `Condition Report draft #${result.draft_id}`;
    const missing = result.readiness?.missing_labels ?? [];
    const elements = result.elements ?? [];
    const attachments = result.attachments ?? [];

    return (
      <Box
        data-testid="aa-cr-draft-created"
        sx={{
          mt: 0,
          mb: 1,
          borderRadius: "0 0 6px 6px",
          border: `1px solid ${grey[300]}`,
          borderTop: "none",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.6,
            borderTop: `1px solid ${grey[300]}`,
            bgcolor: blue[50],
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 16, color: blue[700] }} />
          <Typography
            variant="caption"
            sx={{
              color: grey[800],
              fontSize: "0.75rem",
              fontWeight: 600,
              flex: 1,
            }}
          >
            {heading}
          </Typography>
        </Box>

        <Box sx={{ px: 1.5, py: 1, bgcolor: grey[50] }}>
          {result.title && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: "0.85rem",
                color: grey[900],
                mb: 0.5,
              }}
            >
              {result.title}
            </Typography>
          )}
          {result.fields.length > 0 && (
            <Box
              component="dl"
              data-testid="aa-cr-draft-fields"
              sx={{
                m: 0,
                display: "grid",
                gridTemplateColumns: "max-content 1fr",
                columnGap: 1,
                rowGap: 0.35,
                alignItems: "center",
              }}
            >
              {result.fields.map((field) => (
                <Box key={field.key} sx={{ display: "contents" }}>
                  <Typography
                    component="dt"
                    variant="caption"
                    sx={{ color: grey[700], fontWeight: 600 }}
                  >
                    {field.label}
                  </Typography>
                  <Box
                    component="dd"
                    sx={{
                      m: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8rem",
                        color: grey[800],
                        wordBreak: "break-word",
                      }}
                    >
                      {formatValue(field.value)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {elements.length > 0 && (
            <Box
              data-testid="aa-cr-draft-elements"
              sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}
            >
              {elements.map((element) => {
                const mark = MARK[element.status] ?? MARK.missing;
                return (
                  <Tooltip key={element.key} title={element.note || ""}>
                    <Box
                      data-testid={`aa-cr-element-${element.key}`}
                      data-status={element.status}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 0.75,
                        py: 0.2,
                        borderRadius: 1,
                        border: `1px solid ${mark.color}`,
                        color: mark.color,
                        fontSize: "0.72rem",
                        fontWeight: 600,
                      }}
                    >
                      <span aria-hidden>{mark.glyph}</span>
                      {element.label}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          )}
          {attachments.length > 0 && (
            <Typography
              data-testid="aa-cr-draft-attachments"
              variant="caption"
              sx={{ display: "block", mt: 0.75, color: grey[700] }}
            >
              📎 {attachments.join(", ")}
            </Typography>
          )}
          {result.assessment === "unavailable" && (
            <Typography
              data-testid="aa-cr-draft-assessment"
              variant="caption"
              sx={{ display: "block", mt: 0.75, color: grey[600] }}
            >
              AI quality check is offline — assess it on the draft page.
            </Typography>
          )}
          {missing.length > 0 && (
            <Typography
              data-testid="aa-cr-draft-missing"
              variant="caption"
              sx={{
                display: "block",
                mt: 0.75,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: amber[50],
                color: "#6b5300",
                fontWeight: 600,
              }}
            >
              Still required: {missing.join(", ")}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            bgcolor: grey[50],
            borderTop: `1px solid ${grey[200]}`,
          }}
        >
          <Button
            onClick={() =>
              window.open(crDraftPath(result.draft_id), "_blank", "noopener")
            }
            variant="contained"
            size="small"
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: "none", fontSize: "0.8rem" }}
          >
            Open draft
          </Button>
          <Typography variant="caption" sx={{ color: grey[600], ml: "auto" }}>
            Not submitted — review and submit from the draft page.
          </Typography>
        </Box>
      </Box>
    );
  },
);

CrDraftCreatedCard.displayName = "CrDraftCreatedCard";

export default CrDraftCreatedCard;
